/** First-touch UTM attribution across landing → signup → email confirm / OAuth. */

export const UTM_ATTRIBUTION_STORAGE_KEY = "kryajet_utm_attribution_v1";

export type UtmAttribution = {
  utm_source: string | null;
  utm_campaign: string | null;
  utm_medium: string | null;
  landing_path: string | null;
  captured_at: string;
};

const UTM_KEYS = ["utm_source", "utm_campaign", "utm_medium"] as const;

function normalizeParam(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 200);
}

function readSearchParams(search: string): URLSearchParams {
  try {
    const qs = search.startsWith("?") ? search : `?${search}`;
    return new URLSearchParams(qs);
  } catch {
    return new URLSearchParams();
  }
}

export function parseUtmFromSearch(search: string): Omit<UtmAttribution, "captured_at" | "landing_path"> {
  const params = readSearchParams(search);
  return {
    utm_source: normalizeParam(params.get("utm_source")),
    utm_campaign: normalizeParam(params.get("utm_campaign")),
    utm_medium: normalizeParam(params.get("utm_medium")),
  };
}

export function hasAnyUtm(attrs: {
  utm_source?: string | null;
  utm_campaign?: string | null;
  utm_medium?: string | null;
}): boolean {
  return Boolean(attrs.utm_source || attrs.utm_campaign || attrs.utm_medium);
}

function writeAttribution(attrs: UtmAttribution): void {
  const raw = JSON.stringify(attrs);
  try {
    localStorage.setItem(UTM_ATTRIBUTION_STORAGE_KEY, raw);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(UTM_ATTRIBUTION_STORAGE_KEY, raw);
  } catch {
    /* ignore */
  }
}

function readRaw(): UtmAttribution | null {
  if (typeof window === "undefined") return null;
  for (const store of [localStorage, sessionStorage]) {
    try {
      const raw = store.getItem(UTM_ATTRIBUTION_STORAGE_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as UtmAttribution;
      if (!parsed || typeof parsed !== "object") continue;
      return {
        utm_source: normalizeParam(parsed.utm_source),
        utm_campaign: normalizeParam(parsed.utm_campaign),
        utm_medium: normalizeParam(parsed.utm_medium),
        landing_path: normalizeParam(parsed.landing_path),
        captured_at: typeof parsed.captured_at === "string" ? parsed.captured_at : new Date().toISOString(),
      };
    } catch {
      /* ignore */
    }
  }
  return null;
}

/** Persist UTMs from the current URL (first-touch: keep existing values). */
export function persistUtmFromUrl(): UtmAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const fromUrl = parseUtmFromSearch(window.location.search);
    if (!hasAnyUtm(fromUrl)) return getStoredUtmAttribution();

    const existing = readRaw();
    // First-touch: do not overwrite source/campaign already captured.
    if (existing && hasAnyUtm(existing)) {
      return existing;
    }

    const next: UtmAttribution = {
      utm_source: fromUrl.utm_source,
      utm_campaign: fromUrl.utm_campaign,
      utm_medium: fromUrl.utm_medium,
      landing_path: `${window.location.pathname}${window.location.search}`.slice(0, 500),
      captured_at: new Date().toISOString(),
    };
    writeAttribution(next);
    return next;
  } catch {
    return null;
  }
}

export function persistUtmFromPath(pathWithSearch: string): UtmAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const u = new URL(pathWithSearch, window.location.origin);
    const fromUrl = parseUtmFromSearch(u.search);
    if (!hasAnyUtm(fromUrl)) return getStoredUtmAttribution();

    const existing = readRaw();
    if (existing && hasAnyUtm(existing)) return existing;

    const next: UtmAttribution = {
      utm_source: fromUrl.utm_source,
      utm_campaign: fromUrl.utm_campaign,
      utm_medium: fromUrl.utm_medium,
      landing_path: `${u.pathname}${u.search}`.slice(0, 500),
      captured_at: new Date().toISOString(),
    };
    writeAttribution(next);
    return next;
  } catch {
    return null;
  }
}

export function getStoredUtmAttribution(): UtmAttribution | null {
  const attrs = readRaw();
  if (!attrs || !hasAnyUtm(attrs)) return null;
  return attrs;
}

/** Payload for `supabase.auth.signUp({ options: { data } })`. */
export function buildSignupUtmMetadata(
  attrs: UtmAttribution | null = getStoredUtmAttribution(),
): Record<string, string> {
  if (!attrs || !hasAnyUtm(attrs)) return {};
  const data: Record<string, string> = {};
  if (attrs.utm_source) data.utm_source = attrs.utm_source;
  if (attrs.utm_campaign) data.utm_campaign = attrs.utm_campaign;
  if (attrs.utm_medium) data.utm_medium = attrs.utm_medium;
  if (attrs.landing_path) data.utm_landing_path = attrs.landing_path;
  return data;
}

export function clearStoredUtmAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(UTM_ATTRIBUTION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(UTM_ATTRIBUTION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Append stored (or provided) UTMs onto an auth/signup path. */
export function appendUtmToPath(
  path: string,
  attrs: UtmAttribution | null = getStoredUtmAttribution(),
): string {
  if (!attrs || !hasAnyUtm(attrs)) return path;
  try {
    const u = new URL(path, "https://kryajet.local");
    for (const key of UTM_KEYS) {
      const value = attrs[key];
      if (value && !u.searchParams.get(key)) {
        u.searchParams.set(key, value);
      }
    }
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return path;
  }
}

export type UtmParamInput = {
  utm_source?: string | null;
  utm_campaign?: string | null;
  utm_medium?: string | null;
};

/** Build an absolute URL with UTM query params (overwrites matching keys when provided). */
export function buildTrackedUrl(input: {
  origin: string;
  path: string;
  utm?: UtmParamInput | null;
}): string {
  const origin = input.origin.replace(/\/$/, "");
  const path = input.path.startsWith("/") ? input.path : `/${input.path}`;
  try {
    const u = new URL(path, `${origin}/`);
    u.protocol = new URL(origin).protocol;
    u.host = new URL(origin).host;
    const utm = input.utm;
    if (utm) {
      for (const key of UTM_KEYS) {
        const value = normalizeParam(utm[key] ?? null);
        if (value) u.searchParams.set(key, value);
      }
    }
    return u.toString();
  } catch {
    const qs = new URLSearchParams();
    const utm = input.utm;
    if (utm) {
      for (const key of UTM_KEYS) {
        const value = normalizeParam(utm[key] ?? null);
        if (value) qs.set(key, value);
      }
    }
    const query = qs.toString();
    return `${origin}${path}${query ? `?${query}` : ""}`;
  }
}

/** Copy UTM_* params from a source URLSearchParams onto a destination URL string. */
export function appendUtmSearchParams(targetUrl: string, source: URLSearchParams): string {
  try {
    const u = new URL(targetUrl);
    for (const key of UTM_KEYS) {
      const value = normalizeParam(source.get(key));
      if (value && !u.searchParams.get(key)) {
        u.searchParams.set(key, value);
      }
    }
    return u.toString();
  } catch {
    return targetUrl;
  }
}

export { UTM_KEYS };
